import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useResource } from '../../hooks';
import { staffApi } from '../../staffApi';
import { Callout, Input } from '../../components/primitives';
import { ErrorNotice, Field } from '../../components/ui';

const confirmPhrase = 'leave safe mode';

/**
 * Safe mode, as staff see it (RES-006). After a recovery Commerce Ops
 * resumes and starts nothing: every operation that was running waits for a
 * decision on its run page. Staff leave safe mode once every server was
 * checked; the agents then accept commands again.
 *
 * The status and its controls belong to the organizations page (CO-184,
 * OR22), where staff decide things for the platform. Every other staff page
 * shows only a one-line notice, and only while safe mode is on or its state
 * could not be read (`compact`); a quiet platform adds nothing to them.
 *
 * What the page says is what the platform reports: "Safe mode is off" is the
 * flag, not a verdict that everything runs well. Entering safe mode is a
 * platform-wide stop, so it is a real button in the quiet danger style, and
 * the form that confirms it sits beside the status, never inside a callout.
 */
export function StaffPlatformPanel({ compact = false }: { compact?: boolean }) {
  const platform = useResource(() => staffApi.platform(), [], undefined, { key: 'staff-platform' });
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [entering, setEntering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function submit(input: { enabled: boolean; reason?: string; confirm?: string }) {
    setBusy(true);
    setError(null);
    try {
      await staffApi.setSafeMode(input);
      setConfirm('');
      setReason('');
      setEntering(false);
      platform.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  if (!platform.data) {
    if (platform.error) {
      // Not knowing the platform's state is shown, not left out.
      return (
        <ErrorNotice
          error={platform.error}
          onRetry={platform.reload}
          nextStep="The platform's safe-mode state could not be read, so nothing here says whether it is on."
        />
      );
    }
    return compact ? null : <p className="muted" role="status">Reading the platform’s state…</p>;
  }

  const since = platform.data.since ? new Date(platform.data.since).toLocaleString() : 'a recovery';

  if (compact) {
    if (!platform.data.safe_mode) return null;
    return (
      <Callout tone="warning" title={`Safe mode is on since ${since}`} testId="staff-safe-mode-notice">
        Nothing is started. <Link to="/staff">Open the organizations page</Link> to review it or leave safe mode.
      </Callout>
    );
  }

  if (platform.data.safe_mode) {
    return (
      <section className="staff-platform" aria-label="Platform safe mode" data-testid="staff-safe-mode">
        <Callout tone="warning" title={`Safe mode since ${since}`}>
          <p>{platform.data.reason}</p>
          <p>
            Nothing is resumed or started. Operations that were running wait for a decision; unstarted ones were cancelled.
            Check every server, then leave safe mode: the agents accept commands again, and each waiting run is decided on its
            own run page.
          </p>
        </Callout>
        <form
          className="form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void submit({ enabled: false, confirm });
          }}
        >
          <Field label={`Type “${confirmPhrase}” to confirm`}>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          </Field>
          <ErrorNotice error={error} />
          <div>
            <button className="button button--danger" type="submit" disabled={busy || confirm.trim() !== confirmPhrase}>
              {busy ? 'Leaving safe mode' : 'Leave safe mode'}
            </button>
          </div>
        </form>
      </section>
    );
  }

  if (!entering) {
    return (
      <section className="staff-platform staff-platform--normal" aria-label="Platform safe mode" data-testid="staff-platform-normal">
        <p className="staff-platform__status">
          <strong>Safe mode</strong> <span className="muted">Off</span>
        </p>
        <button type="button" className="button button--danger-quiet button--small" onClick={() => setEntering(true)}>
          Enter safe mode
        </button>
      </section>
    );
  }
  return (
    <form
      className="form panel"
      aria-label="Enter safe mode"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        void submit({ enabled: true, reason });
      }}
    >
      <p>
        Safe mode stops every operation: running ones wait for a decision, unstarted ones are cancelled. Use it after
        restoring the control plane&apos;s database, or when the servers&apos; state is in doubt.
      </p>
      <Field label="Why">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ErrorNotice error={error} />
      <div className="form__actions">
        <button className="button button--secondary" type="button" onClick={() => setEntering(false)} disabled={busy}>
          Cancel
        </button>
        <button className="button button--danger" type="submit" disabled={busy || !reason.trim()}>
          {busy ? 'Entering safe mode' : 'Enter safe mode'}
        </button>
      </div>
    </form>
  );
}
